import React from 'react'
import { Link, useHistory } from 'react-router-dom'
import { FormattedMessage, useIntl } from 'react-intl'
import { Formik, Field } from 'formik'
import { useLogin, usePath } from 'lib/core/hooks'
import { Button, Form, Toast } from 'lib/ui'
import { InterceptBlurEvent } from 'lib/ui/components/Form/types'
import { ForgotPassword } from './styled'
import { schema, validation } from './schema'

interface Props {
  shouldRedirectToAccount?: boolean
  onLoginSuccess?: () => void
  emailInterceptBlur?: (e: InterceptBlurEvent<string>) => void
}

export interface FormValues {
  email: string
  password: string
}

export const LoginForm = ({ shouldRedirectToAccount, onLoginSuccess, emailInterceptBlur }: Props) => {
  const history = useHistory()
  const { formatMessage } = useIntl()
  const path = usePath()

  const { login, error, isFetching } = useLogin({
    onSuccess: handleSuccess,
    onFailure: handleFailure
  })

  function handleSuccess () {
    onLoginSuccess?.()
    if (shouldRedirectToAccount) {
      history.replace(path.customer)
    }
  }

  function handleFailure () {
    Toast.toastFailure({ messageId: error })
  }

  function handleSubmit (values: FormValues) {
    login({
      ...values
    })
  }

  return (
    <Formik
      initialValues={schema}
      validationSchema={validation(formatMessage)}
      onSubmit={handleSubmit}
    >
      <Form.Wrapper>
        <Field
          name='email'
          label={formatMessage({
            id: 'customer.account.login.label.email'
          })}
          component={Form.Input}
          isRequired
          interceptBlur={emailInterceptBlur}
          autoFocus
        />

        <Field
          name='password'
          label={formatMessage({
            id: 'customer.account.login.label.password'
          })}
          component={Form.Input}
          type='password'
          isRequired
        />
        <ForgotPassword>
          <Link to={path.forgotPassword}>
            <FormattedMessage
              id='action.forgotPassword'
            />
          </Link>
        </ForgotPassword>

        <Form.Actions.Wrapper>
          <Button
            type='submit'
            isLoading={isFetching}
          >
            <FormattedMessage
              id='action.login'
            />
          </Button>

          <Button
            skin={Button.SKIN.OUTLINE}
            isDisabled={isFetching}
            onClick={() => history.push(path.createAccount)}
          >
            <FormattedMessage
              id='action.createAccount'
            />
          </Button>
        </Form.Actions.Wrapper>
      </Form.Wrapper>
    </Formik>
  )
}
